
import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';


interface Data {
    id: number;
    name: string;
    objectivesCount:number;
    sede:string;
    area:string;
  }

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }
  
  type Order = 'asc' | 'desc';
  
  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
  ) => number {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  } 

  function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  interface HeadCell {
    disablePadding: boolean;
    id:  keyof Data;
    label: string;    
  }
  
  const headCells: readonly HeadCell[] = [
    {
      id: 'name',      
      disablePadding: false,
      label: 'Nombre de colaborador',
    },
    {
      id: 'sede',    
      disablePadding: false,
      label: 'Sede',
    },
    {
      id: 'area',       
      disablePadding: false,
      label: 'Área o proyecto',
    },
   
    {
      id: 'id',       
      disablePadding: false,
      label: 'Opciones',
    } 
  ];

    interface EnhancedTableProps {
      onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
      order: Order;
      orderBy: string;
      rowCount: number;
    }

   function EnhancedTableHead(props: EnhancedTableProps) {  
        const { order, orderBy, onRequestSort } =
          props;
        const createSortHandler =
          (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
            onRequestSort(event, property);
          };
      
        return (
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  className="tableHeader"
                  key={headCell.id}                   
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                   <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={createSortHandler(headCell.id)}
                    className="labelHeader"                     
                  >
                    {headCell.label}             
                  </TableSortLabel>
                </TableCell>
              ))} 
            </TableRow>
          </TableHead>
        );
      }


const TableCollaboratorsCampaignInfo = ({rows,CollaboratorRowOptions}:any) => {
    const [order, setOrder] = React.useState<Order>("desc");
    const [orderBy, setOrderBy] = React.useState<keyof Data>("id");  
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [page, setPage] = React.useState(0);
       
    const handleRequestSort = (
          event: React.MouseEvent<unknown>,
          property: keyof Data
        ) => {
          const isAsc = orderBy === property && order === "asc";
          setOrder(isAsc ? "desc" : "asc");
          setOrderBy(property);
        };

        const handleChangePage = (event: unknown, newPage: number) => {      
            setPage(newPage);
        };  

         const handleChangeRowsPerPage = (
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            };       
     // Avoid a layout jump when reaching the last page with empty rows.
        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;    
        
        const visibleRows = React.useMemo(
          () =>
            stableSort(rows, getComparator(order, orderBy)).slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage
            ),
          [order, orderBy, page, rows, rowsPerPage]
        );            
        
        return (
            <Box sx={{ width: "100%" }}>
              <Paper sx={{ width: "100%"}}>
                <TableContainer>
                  <Table
                    sx={{ minWidth: 600 }}
                    aria-labelledby="tableTitle"             
                  >
                    <EnhancedTableHead                     
                      order={order}
                      orderBy={orderBy}                     
                      onRequestSort={handleRequestSort}
                      rowCount={rows.length}
                    />
                    <TableBody>
                      {visibleRows.map((row, index) => {
                        return (
                          <TableRow             
                            hover                              
                            tabIndex={-1}
                            key={row.id}      
                            sx={{ cursor: "pointer" }}                
                          >
                           <TableCell >
                                    {row.name}
                            </TableCell>
                             <TableCell align="left">{row.sede}</TableCell>
                            <TableCell align="left">{row.area}</TableCell>                            
                            <TableCell  >
                                <CollaboratorRowOptions {...row} />                            
                           </TableCell> 
                          </TableRow>
                        );
                      })} 
                      {emptyRows > 0 && (
                        <TableRow>
                          <TableCell colSpan={11} />
                        </TableRow>
                      )}
                    </TableBody> 
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage='Número de registros'    
                />
              </Paper>        
            </Box>
          );
}

export default TableCollaboratorsCampaignInfo

